import type {
    MarkingEditorState,
    ExtendedMarkingFormData,
    TextAlignment,
    FontFamily,
} from '../../types'
import { DEFAULT_COLORS } from '../../constants'
import { clampFontSize } from '../validation'
import {
    validateAlignment,
    validateFontFamily,
} from '../validation/fieldValidation'
import type { MarkingEditorScale } from '../persistence/persistence'

/**
 * Миграция старых данных маркировки в новый формат
 * Преобразует mark_media_color (темная/светлая) в hex цвет
 * @param data - данные из БД в старом формате
 * @returns данные в новом формате MarkingEditorState с опциональным scale
 */
export function migrateOldMarkingData(
    data: Partial<ExtendedMarkingFormData>
): Partial<MarkingEditorState> & { scale?: MarkingEditorScale | null } {
    const migrated: Partial<MarkingEditorState> & {
        scale?: MarkingEditorScale | null
    } = {}

    // Миграция mark_media_color (темная/светлая) в hex
    if (data.mark_media_color) {
        migrated.markMediaColor = migrateColor(data.mark_media_color)
    }

    // Миграция остальных полей (если они есть в старых данных)
    if (data.mark_media !== undefined) {
        migrated.markMedia = data.mark_media
    }
    if (data.mark_media_text !== undefined) {
        migrated.markMediaText = data.mark_media_text
    }

    // Новые поля (если есть в данных) - будут добавлены в схему БД в SYN-829
    if (
        data.mark_media_font_size !== undefined &&
        data.mark_media_font_size !== null
    ) {
        migrated.fontSize = clampFontSize(Number(data.mark_media_font_size)) // Явно приводим к числу и применяем clamp
    }
    if (data.mark_media_alignment) {
        // Валидируем выравнивание через функцию валидации вместо type assertion
        migrated.alignment = validateAlignment(data.mark_media_alignment)
    }
    if (data.mark_media_font_family) {
        // Валидируем шрифт через функцию валидации вместо type assertion
        migrated.fontFamily = validateFontFamily(data.mark_media_font_family)
    }
    if (
        data.mark_media_has_background !== undefined &&
        data.mark_media_has_background !== null
    ) {
        migrated.hasBackground = data.mark_media_has_background
    }
    if (data.mark_media_background_color) {
        migrated.backgroundColor = data.mark_media_background_color
    }
    if (
        data.mark_media_position_x !== undefined &&
        data.mark_media_position_x !== null &&
        data.mark_media_position_y !== undefined &&
        data.mark_media_position_y !== null
    ) {
        migrated.position = migratePosition(
            data.mark_media_position_x,
            data.mark_media_position_y
        )
    }

    // Создаем scale из полей БД, если они все присутствуют
    if (
        data.mark_media_canvas_width !== undefined &&
        data.mark_media_canvas_width !== null &&
        data.mark_media_canvas_height !== undefined &&
        data.mark_media_canvas_height !== null &&
        data.mark_media_original_width !== undefined &&
        data.mark_media_original_width !== null &&
        data.mark_media_original_height !== undefined &&
        data.mark_media_original_height !== null
    ) {
        migrated.scale = {
            canvasWidth: Number(data.mark_media_canvas_width),
            canvasHeight: Number(data.mark_media_canvas_height),
            originalWidth: Number(data.mark_media_original_width),
            originalHeight: Number(data.mark_media_original_height),
        }
    }

    return migrated
}

/**
 * Миграция цвета из старого формата (темная/светлая) в hex
 * @param color - цвет в старом формате или уже hex
 * @returns hex цвет
 */
export function migrateColor(color: string): string {
    if (color === DEFAULT_COLORS.light) {
        return DEFAULT_COLORS.light
    }
    if (color === DEFAULT_COLORS.dark) {
        return DEFAULT_COLORS.dark
    }
    // Если уже hex цвет, оставляем как есть
    return color
}

/**
 * Миграция позиции из отдельных координат в объект позиции
 * @param x - координата X
 * @param y - координата Y
 * @returns объект позиции
 */
export function migratePosition(
    x: number,
    y: number
): MarkingEditorState['position'] {
    return {
        x,
        y,
    }
}

/**
 * Миграция данных из старого формата в новый
 * Обертка для совместимости с существующим кодом
 * @param data - данные в старом формате
 * @returns данные в новом формате
 */
export function migrateFromOldFormat(
    data: Partial<ExtendedMarkingFormData>
): Partial<MarkingEditorState> {
    return migrateOldMarkingData(data)
}
